
/**
 * Utility functions for coordinate transformations in the freeform canvas
 */

export interface Transform {
  x: number;
  y: number;
  scale: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface CanvasSize {
  width: number;
  height: number;
}

/**
 * Convert screen coordinates to canvas coordinates
 * Accounts for canvas pan, zoom, and center offset
 */
export const screenToCanvas = (
  screenPoint: Point,
  transform: Transform,
  canvasSize: CanvasSize
): Point => {
  const { x: panX, y: panY, scale } = transform;
  const centerX = canvasSize.width / 2;
  const centerY = canvasSize.height / 2;
  
  // Remove canvas center offset and pan offset, then scale
  const canvasX = (screenPoint.x - centerX - panX) / scale;
  const canvasY = (screenPoint.y - centerY - panY) / scale;
  
  return { x: canvasX, y: canvasY };
};

/**
 * Convert canvas coordinates to screen coordinates
 * Accounts for canvas pan, zoom, and center offset
 */
export const canvasToScreen = (
  canvasPoint: Point,
  transform: Transform,
  canvasSize: CanvasSize
): Point => {
  const { x: panX, y: panY, scale } = transform;
  const centerX = canvasSize.width / 2;
  const centerY = canvasSize.height / 2;
  
  // Scale first, then add pan offset and canvas center offset
  const screenX = canvasPoint.x * scale + centerX + panX;
  const screenY = canvasPoint.y * scale + centerY + panY;
  
  return { x: screenX, y: screenY };
};

/**
 * Clamp canvas position within reasonable boundaries
 */
export const clampCanvasPosition = (
  position: Point,
  boundarySize: number = 5000
): Point => {
  return {
    x: Math.max(-boundarySize, Math.min(boundarySize, position.x)),
    y: Math.max(-boundarySize, Math.min(boundarySize, position.y))
  };
};

/**
 * Snap position to pixel boundaries to avoid blurry rendering
 */
export const snapToPixel = (position: Point): Point => {
  return {
    x: Math.round(position.x),
    y: Math.round(position.y)
  };
};
